/* istanbul ignore file */

import React, { memo, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useSelector } from '@xstate/react';

import { IllusDelivery_1, IllusEmpty_1 } from '@sg/garnish';

import { formatRadarAddress, Location, type AddressDetails } from '@order/features-locations';

import { useCustomer } from '../../../../../../context/Customer';
import { useLocationSearchFlags } from '../../../../hooks/useLocationSearchFlags';
import { useLocationSearchHelpers } from '../../../../hooks/useLocationSearchHelpers';
import { LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH } from '../../../../machine';
import { LocationResults } from '../../../LocationResults';
import { LocationResultsListInfoMessage } from '../../../LocationResultsListInfoMessage';
import { LocationSearchDeliveryResultWithoutNearbyStore } from './components';

import type { LocationSearchService } from '../../../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchDeliveryResult = memo((props: LocationSearchDeliveryResultProps) => {
  const {
    locationSearchActorRef,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    withSafeAreaInsets,
    onExpandedChange,
  } = props;

  const { formatMessage } = useIntl();
  const { customer } = useCustomer();
  const { addresses: customerAddresses = [] } = customer;

  const { setDeliveryPredictionDetails, setDeliveryCustomerAddress } = useLocationSearchHelpers({
    locationSearchActorRef,
  });

  const { isLoadingPredictions, isLoadingLocations } = useLocationSearchFlags({
    locationSearchActorRef,
  });

  const deliverySearchString = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliverySearchString,
  );

  const deliveryLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryLocation,
  );

  const predictionDeliveryAddresses = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryAddressPredictions,
  );

  // ─── Derived Data ────────────────────────────────────────────────────

  const customerDeliveryAddresses = useMemo(() => {
    return customerAddresses.map((customerAddress): AddressDetails => {
      const {
        id: addressId,
        latitude,
        longitude,
        street,
        zipCode,
        city,
        state,
        country,
        name,
      } = customerAddress;

      const formattedAddress = formatRadarAddress(customerAddress);

      return {
        addressId,
        name: name || '',
        formattedAddress,
        latitude,
        longitude,
        street,
        zipCode,
        city,
        state,
        country,
      };
    });
  }, [customerAddresses]);

  const hasEnteredRequiredCharsForAutocomplete =
    deliverySearchString.length >= LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH;
  const hasCustomerAddresses = customerDeliveryAddresses.length > 0;
  const hasNoDeliveryLocation = !isLoadingLocations && !deliveryLocation;
  const hasPredictions = predictionDeliveryAddresses.length > 0;

  const shouldRenderCustomerAddresses =
    hasNoDeliveryLocation && !hasEnteredRequiredCharsForAutocomplete && hasCustomerAddresses;
  const shouldRenderPredictionLoading =
    hasNoDeliveryLocation && hasEnteredRequiredCharsForAutocomplete && isLoadingPredictions;
  const shouldRenderPredictions =
    hasNoDeliveryLocation && hasEnteredRequiredCharsForAutocomplete && hasPredictions;
  const shouldRenderNoPredictionsView =
    hasNoDeliveryLocation &&
    hasEnteredRequiredCharsForAutocomplete &&
    !isLoadingPredictions &&
    !hasPredictions;
  const shouldRenderIntroMessage = hasNoDeliveryLocation && !isLoadingPredictions;

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderCustomerAddresses) {
    return (
      <Location.SearchAddressesList
        addresses={customerDeliveryAddresses}
        onPress={setDeliveryCustomerAddress}
      />
    );
  }

  if (shouldRenderPredictionLoading) {
    return (
      <Location.SearchPredictionAddressesList
        addresses={predictionDeliveryAddresses}
        isLoading
        onPress={setDeliveryPredictionDetails}
      />
    );
  }

  if (shouldRenderPredictions) {
    return (
      <Location.SearchPredictionAddressesList
        addresses={predictionDeliveryAddresses}
        onPress={setDeliveryPredictionDetails}
      />
    );
  }

  if (shouldRenderNoPredictionsView) {
    return (
      <LocationResultsListInfoMessage
        illustration={IllusEmpty_1}
        illustrationAriaLabel={formatMessage(messages.locationEmptyResultsIllustrationAlt)}
        title={formatMessage(messages.locationNoAutocompleteResult)}
        text={formatMessage(messages.locationTryAnotherSearch)}
      />
    );
  }

  if (shouldRenderIntroMessage) {
    return (
      <LocationResultsListInfoMessage
        illustration={IllusDelivery_1}
        illustrationAriaLabel={formatMessage(messages.locationDeliveryIllustrationAlt)}
        text={formatMessage(messages.locationEnterAddress)}
      />
    );
  }

  // If the searched address is too vague, it may not include a zip code,
  // which makes it impossible for us to deliver to that location.
  // In such cases, we want to prevent the user from proceeding.
  if (deliveryLocation && !deliveryLocation?.zipCode) {
    return (
      <LocationResultsListInfoMessage
        illustration={IllusEmpty_1}
        illustrationAriaLabel={formatMessage(messages.locationEmptyResultsIllustrationAlt)}
        title={formatMessage(messages.locationDeliveryZipCodeNotAvailableTitle)}
        text={formatMessage(messages.locationDeliveryZipCodeNotAvailableBody)}
      />
    );
  }

  if (deliveryLocation && !deliveryLocation?.restaurantSlug) {
    return (
      <LocationSearchDeliveryResultWithoutNearbyStore
        locationSearchActorRef={locationSearchActorRef}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LocationResults
      locationSearchActorRef={locationSearchActorRef}
      interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      withSafeAreaInsets={withSafeAreaInsets}
      onExpandedChange={onExpandedChange}
    />
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationDeliveryIllustrationAlt: {
    defaultMessage: 'Delivery courier riding a scooter',
    description: 'Location | Delivery Illustration | Alt',
  },
  locationEmptyResultsIllustrationAlt: {
    defaultMessage: 'Seedless pea pod',
    description: 'Location | Empty Results Illustration | Alt',
  },
  locationNoAutocompleteResult: {
    defaultMessage: `Ah snap peas! We couldn't find any locations.`,
    description: 'Location | No Autocomplete Result',
  },
  locationTryAnotherSearch: {
    defaultMessage: 'Try another search',
    description: 'Location | Try Another Search',
  },
  locationEnterAddress: {
    defaultMessage: 'Enter an address above to get started.',
    description: 'Location | Enter Address',
  },
  locationDeliveryZipCodeNotAvailableTitle: {
    defaultMessage: 'Sorry, the address you provided is not valid.',
    description: 'Location | Delivery | Zip Code Not Available Title',
  },
  locationDeliveryZipCodeNotAvailableBody: {
    defaultMessage:
      'Please make sure to include the complete street address, including the zip code',
    description: 'Location | Delivery | Zip Code Not Available Body',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchDeliveryResultProps = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  interactedLocationRestaurantSlug: string | undefined;
  interactedLocationDeliveryAddressId: string | undefined;
  withSafeAreaInsets?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
};
